import type { PropsWithChildren } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { clone, get } from 'lodash-es';
import { useHistory, useLocation } from 'react-router-dom';
import { useIsFetching, useIsMutating } from '@zg-rentals/react-query5';

import { Box, Button, ButtonGroup, Heading, TextButton } from '@zillow/constellation-10';
import { LoadingButton, LoadingHeading, LoadingText } from '@zg-rentals/loading-components';

import EditBlocker from '../../shared/components/SectionWrapper/EditBlocker';
import BackAndSubmit from '../../shared/components/BackAndSubmit';
import { Section } from '../../shared/models';
import { usePreSubmitCallback } from '../../shared/contexts/PreSubmitCallbackContext';
import { useSectionContext } from '../../shared/contexts/SectionContext';
import {
  useCancelSigningMutation,
  useCompletionStatusQuery,
  useGetNextNodeMutation,
  useGetPreviousNodeMutation,
  useLeaseFromOuidQuery,
  useLeaseQuery,
  useSubmitWizardDataMutation,
  useWebAnalyticsForListingQuery,
} from '../../shared/utils/queries';
import { LEASE_VIEW_TYPES } from '../../shared/models/Lease';
import { LeaseArtifactType } from '@zg-rentals/ts-leasing-client';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import { CANCEL, CANCELLATION_REASON } from '../../shared/constants/stringConstants';
import { emitYesEditLeaseEvent } from '../../analytics/events';
import MultiMandate from '../../shared/components/Widgets/MultiMandate';
import SectionCategoryIntro from '../../shared/components/Widgets/SectionCategoryIntro';
import { putNodeIdInSearch } from '../../shared/utils/urlUtils';
import {
  NEXT_TO_SECTION_TEXT,
  NEXT_TO_SUMMARY_TEXT,
  SAVE_AND_CLOSE_TEXT,
  SAVE_CHANGES_TEXT,
  SECTION_INTRO_CATEGORY_INDEX,
  SectionIntroLabels,
  Transition,
  VIEW_SUMMARY,
} from './SectionWrapper/constants';
import LeaseModal from './Containers/LeaseModal';
import { determineTypeOfLease } from '../constants/analytics';
import { useLeasingUrlParams } from '../hooks';
import { isColandlordPath } from '../constants/routeConstants';

type Props = PropsWithChildren<{
  leaseId?: string;
  propertyId?: string;
  setNodeIdInSearch?: boolean;
  onSubmitMiniWorkflow?: (section: Section) => Promise<void>;
  renderModalView?: boolean;
  modalOnClose?: (shouldInvalidate?: boolean) => void;
  modalIsOpen?: boolean;
  modalHeading?: string;
  onLastSectionSubmitted?: () => void;
}>;

export function SectionWrapper(props: Props) {
  const {
    children,
    leaseId: propLeaseId,
    propertyId: propPropertyId,
    setNodeIdInSearch,
    onSubmitMiniWorkflow,
    renderModalView,
    modalOnClose,
    modalIsOpen,
    modalHeading,
    onLastSectionSubmitted,
  } = props;

  const { leaseId: urlLeaseId, propertyId: urlPropertyId, viewType } = useLeasingUrlParams();
  const history = useHistory();
  const location = useLocation();

  const leaseId = propLeaseId || urlLeaseId;
  const propertyId = propPropertyId || urlPropertyId;

  const { section, setSection, setSectionValues: contextSetSectionValues } = useSectionContext();
  const isWizardView = viewType === LEASE_VIEW_TYPES.WIZARD;

  const isColandlord = isColandlordPath(location.pathname);
  const queryParams = new URLSearchParams(window.location.search);
  const ouid = queryParams.get('ouid');

  const landlordLeaseQuery = useLeaseQuery(leaseId, { enabled: !!leaseId && !isColandlord });
  const colandlordLeaseQuery = useLeaseFromOuidQuery(ouid, isColandlord);
  const lease = isColandlord ? colandlordLeaseQuery.data : landlordLeaseQuery.data;

  const submitWizardDataMutation = useSubmitWizardDataMutation();
  const getNextNodeMutation = useGetNextNodeMutation();
  const getPreviousNodeMutation = useGetPreviousNodeMutation();

  const isFetching = useIsFetching() > 0;
  const isMutating = useIsMutating() > 0;
  const isLoading = isFetching || isMutating;

  useWebAnalyticsForListingQuery(propertyId, { enabled: !!propertyId });

  const completionStatusQuery = useCompletionStatusQuery(leaseId, {
    enabled: !!leaseId && !!lease && isWizardView,
  });

  const [sectionFromComponentState, setSectionFromComponentState] = useState<Section | null>(section);

  const effectiveSection = contextSetSectionValues && section ? section : sectionFromComponentState;

  const lastValidStateRef = useRef<{
    lease: typeof lease;
    section: typeof section;
    effectiveSection: typeof effectiveSection;
  } | null>(null);

  if (lease != null && section != null && effectiveSection != null) {
    lastValidStateRef.current = { lease, section, effectiveSection };
  }

  const displayLease = lease ?? lastValidStateRef.current?.lease ?? null;
  const displaySection = section ?? lastValidStateRef.current?.section ?? null;
  const displayEffectiveSection = effectiveSection ?? lastValidStateRef.current?.effectiveSection ?? null;

  const updateSectionValues = useCallback(
    (keyValuePairs: object) => {
      if (contextSetSectionValues) {
        contextSetSectionValues(keyValuePairs);
      } else {
        const updatedSection = clone(sectionFromComponentState);
        updatedSection.setFieldValues(keyValuePairs);
        setSectionFromComponentState(updatedSection);
      }
    },
    [contextSetSectionValues, sectionFromComponentState],
  );

  const { getPreSubmitCallback } = usePreSubmitCallback();

  const completionStatus = completionStatusQuery.data || null;

  const { user } = useRentalManagerUser();
  const webAnalyticsQuery = useWebAnalyticsForListingQuery(propertyId, { enabled: !!propertyId });
  const zrm_listing_info = webAnalyticsQuery.data;
  const cancelSigningMutation = useCancelSigningMutation();

  const leaseForLogic = displayLease!;
  const effectiveSectionForLogic = displayEffectiveSection!;

  const [hasVisitedPreviousSection, setHasVisitedPreviousSection] = useState(false);
  const [transitionName, setTransitionName] = useState<Transition>(Transition.SlideUp);
  const [currentSectionCategoryIndex, setCurrentSectionCategoryIndex] = useState(SECTION_INTRO_CATEGORY_INDEX);
  const [signingWasCanceled, setSigningWasCanceled] = useState(false);

  useEffect(() => {
    if (section && !sectionFromComponentState) {
      setSectionFromComponentState(clone(section));
    }
  }, [section, sectionFromComponentState]);

  useEffect(() => {
    if (section && sectionFromComponentState) {
      const nextSectionCategoryIndex = section.getSectionCategoryIndex();
      const prevSectionCategoryIndex = sectionFromComponentState.getSectionCategoryIndex();

      if (nextSectionCategoryIndex > prevSectionCategoryIndex) {
        setTransitionName(Transition.SlideLeft);
      } else if (nextSectionCategoryIndex < prevSectionCategoryIndex) {
        setTransitionName(Transition.SlideRight);
      }
    }
  }, [section, sectionFromComponentState]);

  const previousUiSectionRef = useRef<string | null>(null);
  const previousNodeIdRef = useRef<number | null>(null);

  const handleGetPrevious = useCallback(async () => {
    if (!sectionFromComponentState) return;

    setTransitionName(Transition.SlideDown);

    const response = await getPreviousNodeMutation.mutateAsync({
      currentNodeId: sectionFromComponentState.currentNodeId,
      leaseId,
    });

    const newSection = new Section(response);

    setSection(newSection);

    if (setNodeIdInSearch && newSection.currentNodeId) {
      putNodeIdInSearch(`${newSection.currentNodeId}`, history.push);
    }

    setCurrentSectionCategoryIndex(sectionFromComponentState.getSectionCategoryIndex());
  }, [leaseId, sectionFromComponentState, setNodeIdInSearch, history, setSection, getPreviousNodeMutation]);

  const setSectionValues = useCallback(
    (keyValuePairs: object) => {
      updateSectionValues(keyValuePairs);
    },
    [updateSectionValues],
  );

  const setTestingHelper = useCallback(() => {
    if (typeof window !== 'undefined') {
      // @ts-expect-error - window.__TESTING__ is a test helper
      const testingHelper = window.__TESTING__ ?? {};

      if (section != null) {
        testingHelper.setSectionValues = (keyValuePairs) => {
          setSectionValues(keyValuePairs);
        };
      }

      // @ts-expect-error - window.__TESTING__ is a test helper
      window.__TESTING__ = testingHelper;
    }
  }, [section, setSectionValues]);

  useEffect(() => {
    const currentUiSection = section?.uiSection;
    const currentNodeId = section?.currentNodeId;
    const previousUiSection = previousUiSectionRef.current;
    const previousNodeId = previousNodeIdRef.current;

    if (currentUiSection == null || currentNodeId == null) {
      previousUiSectionRef.current = null;
      previousNodeIdRef.current = null;
      return;
    }

    const sectionTypeChanged = previousUiSection !== currentUiSection;
    const nodeIdChanged = previousNodeId !== currentNodeId;

    if (sectionTypeChanged || nodeIdChanged) {
      setSectionFromComponentState(clone(section));

      setTestingHelper();

      if (previousUiSection && sectionTypeChanged && !hasVisitedPreviousSection) {
        setHasVisitedPreviousSection(true);
      }

      if (setNodeIdInSearch && sectionTypeChanged) {
        putNodeIdInSearch(`${currentNodeId}`, previousUiSection == null ? history.replace : history.push);
      }
    }

    previousUiSectionRef.current = currentUiSection;
    previousNodeIdRef.current = currentNodeId;
  }, [
    section?.uiSection,
    section?.currentNodeId,
    hasVisitedPreviousSection,
    setNodeIdInSearch,
    history,
    section,
    setTestingHelper,
  ]);

  const onCancelEdit = useCallback(() => {
    switch (viewType) {
      case LEASE_VIEW_TYPES.WIZARD: {
        history.push(leaseForLogic.getSummaryPath());
        return;
      }
      case LEASE_VIEW_TYPES.SUMMARY:
      case LEASE_VIEW_TYPES.SELF_SERVE: {
        setSection(null);
        if (modalOnClose) {
          modalOnClose(signingWasCanceled);
        }
        setSigningWasCanceled(false);
      }
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- lease is a real dependency (leaseForLogic derives from it; oxlint can't see through that indirection), setSection is a stable context setter safe to omit; restoring the original (pre-oxlint) deps array
  }, [lease, viewType, history, modalOnClose, signingWasCanceled]);

  const onSubmitClick = useCallback(async () => {
    if (!effectiveSection) return;

    setSection(effectiveSection);

    const onPreSubmitCallback = getPreSubmitCallback();
    if (onPreSubmitCallback) {
      const pauseExecution = await onPreSubmitCallback();
      if (pauseExecution) return;
    }

    setTransitionName(Transition.SlideUp);

    if (onSubmitMiniWorkflow) {
      await onSubmitMiniWorkflow(effectiveSection);
      return;
    }

    const data = effectiveSection.getSubmitJSON();
    await submitWizardDataMutation.mutateAsync({
      postData: { ...data, leaseId: leaseId as unknown as number },
    });

    if (effectiveSection.hasNext) {
      const response = await getNextNodeMutation.mutateAsync({
        currentNodeId: effectiveSection.currentNodeId,
        leaseId,
      });

      const newSection = new Section(response);

      setSection(newSection);

      if (setNodeIdInSearch && newSection.currentNodeId) {
        putNodeIdInSearch(`${newSection.currentNodeId}`, history.push);
      }
    } else {
      setSection(null);

      if (onLastSectionSubmitted) {
        onLastSectionSubmitted();
      }
    }
  }, [
    effectiveSection,
    leaseId,
    onLastSectionSubmitted,
    getPreSubmitCallback,
    onSubmitMiniWorkflow,
    submitWizardDataMutation,
    setNodeIdInSearch,
    history,
    setSection,
    getNextNodeMutation,
  ]);

  const handleGetStartedClick = useCallback(() => {
    setCurrentSectionCategoryIndex(sectionFromComponentState.getSectionCategoryIndex());
    setTransitionName(Transition.SlideUp);
  }, [sectionFromComponentState]);

  const handleSetSectionFromComponentState = useCallback(
    (newSection: Section) => {
      if (contextSetSectionValues) {
        setSection?.(newSection);
      } else {
        setSectionFromComponentState(clone(newSection));
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
    [contextSetSectionValues],
  );

  // Define renderBackAndSubmit first to avoid circular dependency
  const renderBackAndSubmit = useCallback(() => {
    if (!effectiveSection) return null;

    const hasFieldChanged = effectiveSection.hasChanged();
    const hasVisited = effectiveSectionForLogic.hasVisited;
    const isSelfUpload = viewType === LEASE_VIEW_TYPES.SELF_SERVE;
    const { leaseType } = leaseForLogic;
    const typeOfLease = determineTypeOfLease(leaseType);

    let buttonText;
    if (effectiveSection.uiSection === 'AdditionalDocuments') {
      // Since Additional Document doesn't have any fields, it doesn't make sense to have button say "Save"
      buttonText = VIEW_SUMMARY;
    } else {
      if (!isWizardView && (!isSelfUpload || hasVisited)) {
        buttonText = SAVE_CHANGES_TEXT;
      } else if (effectiveSection.hasNext) {
        buttonText = NEXT_TO_SECTION_TEXT;
      } else if (isSelfUpload) {
        buttonText = SAVE_AND_CLOSE_TEXT;
      } else {
        buttonText = NEXT_TO_SUMMARY_TEXT;
      }
    }

    const showBack = (isWizardView || (isSelfUpload && !hasVisited)) && effectiveSection.hasPrevious;
    return (
      <BackAndSubmit
        isDisabled={isLoading || effectiveSection.hasErrors()}
        isLoading={isLoading}
        hasNext={effectiveSection.hasNext}
        hasPrevious={effectiveSection.hasPrevious}
        getPrevious={handleGetPrevious}
        submitHandler={onSubmitClick}
        hasFieldChanged={hasFieldChanged}
        submitText={buttonText}
        showBack={showBack}
        renderModalView={renderModalView}
        leaseType={typeOfLease}
        uiSection={effectiveSection.uiSection}
        lease={lease}
      />
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [effectiveSection, viewType, lease, isWizardView, isLoading, renderModalView, handleGetPrevious, onSubmitClick]);

  const renderSectionComponent = useCallback(
    (SectionComponent) => {
      if (!effectiveSection) return null;
      const showBackAndSubmit = !renderModalView;

      return (
        <>
          <SectionComponent
            isFetching={isFetching}
            section={effectiveSection}
            setSection={handleSetSectionFromComponentState}
            setSectionValues={setSectionValues}
            leaseId={leaseId}
            lease={lease}
            propertyId={propertyId}
          />
          {effectiveSection.showMandate && effectiveSection.mandates.length > 0 && (
            <Box css={{ marginTop: 'tight' }}>
              <MultiMandate mandates={effectiveSection.mandates} />
            </Box>
          )}
          {showBackAndSubmit && renderBackAndSubmit()}
        </>
      );
    },
    [
      effectiveSection,
      isFetching,
      leaseId,
      lease,
      propertyId,
      renderModalView,
      handleSetSectionFromComponentState,
      setSectionValues,
      renderBackAndSubmit,
    ],
  );

  const renderSection = useCallback(() => {
    if (!effectiveSection) return null;

    const SectionComponent = effectiveSection.getComponent();
    const nextSectionCategoryIndex = effectiveSection.getSectionCategoryIndex();
    const labels = SectionIntroLabels[nextSectionCategoryIndex] || {
      label: '',
      subLabel: '',
      subLabelDescription: '',
      icon: <></>,
    };
    const isTransitioningToNextSection = currentSectionCategoryIndex < nextSectionCategoryIndex;
    const visited = get(completionStatus, ['categoryCompletion', nextSectionCategoryIndex, 'visited']) || false;
    const showComponentIntro =
      isWizardView &&
      isTransitioningToNextSection &&
      (hasVisitedPreviousSection || currentSectionCategoryIndex === SECTION_INTRO_CATEGORY_INDEX) &&
      !visited;

    return SectionComponent ? (
      <div className="section-wrapper">
        <SectionCategoryIntro
          label={labels.label}
          subLabel={labels.subLabel}
          subLabelDescription={labels.subLabelDescription}
          icon={labels.icon}
          transitionName={transitionName}
          getPrevious={handleGetPrevious}
          getStartedClick={handleGetStartedClick}
          hasPrevious={nextSectionCategoryIndex !== 0}
          showComponentIntro={showComponentIntro}
          data-test="section-category-intro"
          lease={lease}
        >
          {renderSectionComponent(SectionComponent)}
        </SectionCategoryIntro>
      </div>
    ) : null;
  }, [
    effectiveSection,
    currentSectionCategoryIndex,
    completionStatus,
    isWizardView,
    hasVisitedPreviousSection,
    transitionName,
    handleGetPrevious,
    handleGetStartedClick,
    lease,
    renderSectionComponent,
  ]);

  const renderEditBlocker = useCallback(() => {
    return <EditBlocker key="edit-blocker" lease={lease} onCancelEdit={onCancelEdit} isWizardView={isWizardView} />;
  }, [lease, onCancelEdit, isWizardView]);

  const renderSectionWrapper = useCallback(() => {
    if (!effectiveSection) return null;

    return (
      <>
        <React.Fragment key={effectiveSectionForLogic.uiSection}>
          {!leaseForLogic.isEditable() ? (
            <EditBlocker key="edit-blocker" lease={leaseForLogic} onCancelEdit={onCancelEdit} isWizardView={false} />
          ) : (
            renderSection()
          )}
        </React.Fragment>
        {children}
      </>
    );
  }, [effectiveSection, leaseForLogic, effectiveSectionForLogic, children, onCancelEdit, renderSection]);

  const handleModalClose = useCallback(() => {
    if (!leaseForLogic.isEditable() && cancelSigningMutation.isPending) {
      return;
    }
    onCancelEdit();
  }, [leaseForLogic, cancelSigningMutation.isPending, onCancelEdit]);

  const renderModalFooter = useCallback((): React.ReactNode => {
    if (!leaseForLogic.isEditable()) {
      async function handleCancelSigningClick() {
        emitYesEditLeaseEvent({ user, listing: zrm_listing_info });

        try {
          await cancelSigningMutation.mutateAsync({
            postData: {
              leaseArtifactId: leaseForLogic.id as unknown as number,
              leaseArtifactType: LeaseArtifactType.LEASE,
              message: CANCELLATION_REASON,
            },
          });
          // Mark that signing was canceled so queries will be invalidated
          setSigningWasCanceled(true);
          onCancelEdit();
        } catch {
          onCancelEdit();
        }
      }

      function handleCancel() {
        onCancelEdit();
      }

      return (
        <ButtonGroup aria-label="make edits">
          <TextButton
            tone="brand"
            onClick={handleCancel}
            disabled={cancelSigningMutation.isPending}
            data-test="go-back"
          >
            {CANCEL}
          </TextButton>
          <Button
            loading={cancelSigningMutation.isPending}
            tone="brand"
            emphasis="filled"
            onClick={handleCancelSigningClick}
          >
            Yes
          </Button>
        </ButtonGroup>
      );
    }
    return renderBackAndSubmit();
  }, [leaseForLogic, renderBackAndSubmit, user, zrm_listing_info, cancelSigningMutation, onCancelEdit]);

  const renderLoadingSkeleton = () => (
    <>
      <LoadingHeading isLoading level={3} loadingWidth="50%" css={{ marginBottom: 'loose' }}>
        Loading
      </LoadingHeading>
      <LoadingHeading
        isLoading
        level={4}
        css={{
          textStyle:
            'responsive.heading-xs' /* oxlint-disable-line constellation/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
        }}
        loadingWidth="30%"
      >
        Loading
      </LoadingHeading>
      <LoadingText isLoading loadingWidth="100%" css={{ marginBottom: 'tight' }}>
        Loading
      </LoadingText>
      <LoadingHeading
        isLoading
        level={4}
        css={{
          textStyle:
            'responsive.heading-xs' /* oxlint-disable-line constellation/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
        }}
        loadingWidth="50%"
      >
        Loading
      </LoadingHeading>
      <LoadingText isLoading loadingWidth="100%" css={{ marginBottom: 'tight' }}>
        Loading
      </LoadingText>
      <LoadingHeading
        isLoading
        level={4}
        css={{
          textStyle:
            'responsive.heading-xs' /* oxlint-disable-line constellation/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
        }}
        loadingWidth="40%"
      >
        Loading
      </LoadingHeading>
      <LoadingText isLoading loadingWidth="100%" css={{ marginBottom: 'loose' }}>
        Loading
      </LoadingText>
      <LoadingHeading
        isLoading
        level={4}
        css={{
          textStyle:
            'responsive.heading-xs' /* oxlint-disable-line constellation/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
        }}
        loadingWidth="30%"
      >
        Loading
      </LoadingHeading>
      <LoadingText isLoading loadingWidth="100%" css={{ marginBottom: 'tight' }}>
        Loading
      </LoadingText>
      <LoadingHeading
        isLoading
        level={4}
        css={{
          textStyle:
            'responsive.heading-xs' /* oxlint-disable-line constellation/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
        }}
        loadingWidth="20%"
      >
        Loading
      </LoadingHeading>
      <LoadingText isLoading loadingWidth="100%">
        Loading
      </LoadingText>
    </>
  );

  const renderLoadingFooterSkeleton = () => (
    <ButtonGroup aria-label="loading actions">
      <LoadingButton isLoading loadingWidth="30%">
        Back
      </LoadingButton>
      <LoadingButton isLoading loadingWidth="30%">
        Continue
      </LoadingButton>
    </ButtonGroup>
  );

  if (displayLease == null || displaySection == null || displayEffectiveSection == null) {
    if (renderModalView) {
      return (
        <LeaseModal
          data-test="section-modal"
          header={<Heading level={1}>{modalHeading ? modalHeading : 'Edit lease'}</Heading>}
          onClose={handleModalClose}
          isOpen={modalIsOpen === true || modalIsOpen === false ? modalIsOpen : true}
          body={renderLoadingSkeleton()}
          footer={renderLoadingFooterSkeleton()}
        />
      );
    }
    return renderLoadingSkeleton();
  }

  if (modalIsOpen === false) {
    return null;
  }

  if (isWizardView && !renderModalView && !displayLease.isEditable()) {
    return renderEditBlocker();
  }

  return renderModalView ? (
    <LeaseModal
      data-test="section-modal"
      header={<Heading level={1}>{modalHeading ? modalHeading : 'Edit lease'}</Heading>}
      onClose={handleModalClose}
      isOpen={modalIsOpen === true || modalIsOpen === false ? modalIsOpen : true}
      body={renderSectionWrapper()}
      footer={renderModalFooter()}
    />
  ) : (
    renderSectionWrapper()
  );
}

export default SectionWrapper;
